import React, { forwardRef, ForwardRefExoticComponent, RefAttributes, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Input as AntInput, InputProps as AntInputProps } from '@vertem-core/vertice-ds';
import MaskedInput from 'antd-mask-input';
import { InputProps } from 'antd/lib/input';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

interface CustomInputProps extends Omit<AntInputProps, 'defaultValue' | 'showCount'> {
  mask?: string;
  customDefaultValue?: string;
  value?: string;
  placeholder?: any;
  disabled?: boolean;
  type?: string;
  loading?: boolean;
  shouldApplyMask?: boolean;
}

interface InputComponent extends ForwardRefExoticComponent<CustomInputProps & RefAttributes<Node>> {
  Password: typeof Password;
}

interface StyledLabelProps {
  $isFocused: boolean;
  $hasValue: boolean;
}

const InputWrapper = styled.div`
  position: relative;
`;

const paddingResponsive = '40px';

const StyledLabel = styled.label<StyledLabelProps>`
  position: absolute;
  top: ${(props) => (props.$isFocused || props.$hasValue ? '8px' : '50%')};
  left: ${paddingResponsive};
  transform: translateY(${(props) => (props.$isFocused || props.$hasValue ? '0%' : '-50%')});
  font-size: ${(props) => (props.$isFocused || props.$hasValue ? '12px' : '16px')};
  color: ${(props) => (props.$isFocused ? 'var(--colorTextInput)' : 'var(--colorTextPlaceholder)')};
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 999;
`;

const StyledInput = styled(AntInput)`
  padding: 20px ${paddingResponsive} 8px ${paddingResponsive};
  height: var(--inputHeight-fml, 48px) !important;

  &:disabled {
    color: #333 !important;
    background-color: var(--color-neutral-light-darker) !important;
    border-color: var(--color-neutral-light-darker) !important;
    opacity: 1 !important;
  }
  
  &.ant-input-disabled {
    color: #333 !important;
    background-color: var(--color-neutral-light-darker) !important;
    border-color: var(--color-neutral-light-darker) !important;
    opacity: 1 !important;
  }
  
  &.ant-input-disabled input {
    color: #333 !important;
    background-color: var(--color-neutral-light-darker) !important;
    border-color: var(--color-neutral-light-darker) !important;
    opacity: 1 !important;
  }
`;
const StyledMaskedInput = styled(MaskedInput)<{ isFocused: boolean; hasValue: boolean }>`
  padding: 20px ${paddingResponsive} 8px ${paddingResponsive};
  height: var(--inputHeight-fml, 48px) !important;
  border-radius: var(--borderRadiusInput) !important;
  border: 1px solid var(--colorTextPlaceholder) !important;
  background-color: var(--colorInput) !important;
  color: ${(props) => (props.isFocused || props.hasValue ? 'var(--colorTextInput)' : 'transparent')} !important;
  transition: color 0.3s ease;

  &:disabled {
    color: #333 !important;
    background-color: var(--color-neutral-light-darker) !important;
    border-color: var(--color-neutral-light-darker) !important;
  }
`;

const LoaderWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 999;
`;

const Input = forwardRef<HTMLInputElement, CustomInputProps>((props:any, ref:any) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(!!props.value);
  const [dynamicMask, setDynamicMask] = useState('');
  const [shouldApplyMask, setShouldApplyMask] = useState(props.shouldApplyMask);
  const [inputType, setInputType] = useState(props.type === 'date' ? 'text' : props.type);
  const inputRef = useRef<HTMLInputElement>(null) as any;

  useEffect(() => {
    const unmaskedValue = props.value;
    const hasValueNow = !!unmaskedValue;
    setHasValue(hasValueNow);
    
    if (props.type === 'document') {
      if (unmaskedValue && unmaskedValue.replace(/_/g, '').replace(/ /g, '').length > 15) {
        setDynamicMask('00.000.000/000-00');
      } else {
        setDynamicMask('000.000.000-0000');
      }
    }
  
    if (props.disabled) {
      setIsFocused(true);
    }
  
    // Para campos de data, sempre usar tipo 'date' se há valor
    if (props.type === 'date') {
      if (unmaskedValue) {
        setInputType('date');
      } else {
        setInputType('text');
      }
    }
  
    // Aplicar máscara se há máscara OU se há valor (para garantir visibilidade)
    if ((props.mask || props.type === 'document') && (unmaskedValue || props.shouldApplyMask)) {
      setShouldApplyMask(true);
    }
  }, [props.value, props.type, props.disabled, props.name, isFocused, props.mask, shouldApplyMask, props.shouldApplyMask]);
  
  // Forçar hasValue para campos desabilitados com valor
  useEffect(() => {
    if (props.disabled && props.value) {
      setHasValue(true);
    }
  }, [props.disabled, props.value, props.name]);

  useEffect(() => {
    if (shouldApplyMask && inputRef.current) {
      inputRef.current.focus();
    }
  }, [shouldApplyMask]);

  const handleFocus = () => {
    setIsFocused(true);
    setShouldApplyMask(true);

    if (props.type === 'date') {
      setInputType('date');
    }
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);

    if (props.type === 'date') {
      setInputType(e.target.value ? 'date' : 'text');
      setHasValue(!!e.target.value);
    }
  };

  const renderLoader = () => {
    if (!props.loading) return null;
    
    return (
      <LoaderWrapper>
        <Spin size="small" style={{marginRight: '5px'}} />
      </LoaderWrapper>
    );
  };

  return (
    <InputWrapper>
      <StyledLabel
        $isFocused={isFocused}
        $hasValue={hasValue}
      >
        {props.placeholder}
      </StyledLabel>
      
      {props.type !== 'date' && (props.mask || props.type === 'document') && shouldApplyMask ? (
        <>
          <StyledMaskedInput
            {...props}
            ref={(node) => {
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
              inputRef.current = node;
            }}
            maskOptions={{
              placeholderChar: ' '
            }}
            mask={props.mask || dynamicMask}
            isFocused={isFocused}
            hasValue={hasValue}
            placeholder={' '}
            autoComplete="off"
            onFocus={handleFocus}
            onBlur={handleBlur}
            // Remover value prop para deixar o Form controlar
          />
          {renderLoader()}
        </>
      ) : (
        <>
          <StyledInput
            {...props}
            type={inputType}
            ref={(node:any) => {
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
              inputRef.current = node;
            }}
            autoComplete="off"
            placeholder={' '}
            onFocus={handleFocus}
            onBlur={handleBlur}
            // Remover value prop para deixar o Form controlar
          />
          {renderLoader()}
        </>
      )}
      
    </InputWrapper>
  );
}) as InputComponent;

Input.displayName = 'Input';

const Password = forwardRef<Node, InputProps>((props, _ref) => {
  const [visible, setVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  useEffect(() => {
    const hasInputValue = props.value !== undefined && props.value !== '';
    setHasValue(hasInputValue);
  }, [props.value]);

  return (
    <InputWrapper>
      <StyledLabel
        $isFocused={isFocused}
        $hasValue={hasValue}
      >
        {props.placeholder}
      </StyledLabel>
      <StyledInput
        {...props}
        type={visible ? 'text' : 'password'}
        name="unique-password-field"
        id="unique-password-field"
        placeholder={undefined}
        suffix={
          visible ? (
            <EyeOutlined onClick={() => setVisible(false)} />
          ) : (
            <EyeInvisibleOutlined onClick={() => setVisible(true)} />
          )
        }
        onFocus={() => setIsFocused(true)}
        onBlur={(e: any) => {
          setIsFocused(false);
          setHasValue(!!e.target.value);
        }}
      />
    </InputWrapper>
  );
});

Password.displayName = 'Input.LoginPassword';

Input.Password = Password;

export default Input;
